<template>
    <!-- Breadcrumb Navigation -->
    <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
        <router-link :to="{ name: 'pages.admin.dashboard' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:home-smile-bold-duotone" /> 
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <router-link :to="{ name: 'pages.admin.services' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.services") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ service?.name || 'Service Details' }}</span>
        </button>
    </div>

    <!-- Main Content -->
    <div class="tw-p-2">
        <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-gray-100">
            
            <!-- Loading State -->
            <div v-if="loading" class="tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
                <div class="tw-text-center">
                    <div class="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-primary-500 tw-mx-auto tw-mb-4"></div>
                    <p class="tw-text-gray-600">Loading service details...</p>
                </div>
            </div>

            <!-- Content -->
            <div v-else>
                <!-- Header Section -->
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                    <div>
                        <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Service Details</h1>
                        <p class="tw-text-sm tw-text-gray-600">View information for "{{ service?.name || 'Loading...' }}"</p>
                    </div>
                    
                    <!-- Action Buttons -->
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <a-button
                            @click="goBack"
                            intent="light"
                            left-icon="solar:arrow-left-linear"
                        >
                            Back to Services
                        </a-button>
                        
                        <a-button
                            as="router-link"
                            :to="{ name: 'pages.admin.services.edit', params: { id: $route.params.id } }"
                            intent="primary"
                            left-icon="solar:pen-bold-duotone"
                        >
                            Edit Service
                        </a-button>
                    </div>
                </div>

                <!-- Service Content -->
                <div v-if="service" class="tw-max-w-4xl">
                    <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
                        <!-- Left Column - Service Information -->
                        <div class="tw-space-y-6">
                            <div>
                                <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Service Information</h2>
                                
                                <!-- Service Name -->
                                <div class="tw-mb-6">
                                    <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Service Name</label>
                                    <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                                        {{ service.name }}
                                    </div>
                                </div>

                                <!-- Service Description -->
                                <div class="tw-mb-6">
                                    <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Description</label>
                                    <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border tw-min-h-[120px]">
                                        {{ service.description || 'No description provided' }}
                                    </div>
                                </div>

                                <!-- Categories -->
                                <div class="tw-mb-6">
                                    <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Categories</label>
                                    <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                                        <div v-if="service.categories && service.categories.length > 0" class="tw-flex tw-gap-2 tw-flex-wrap">
                                            <span
                                                v-for="category in service.categories"
                                                :key="category.id"
                                                class="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium tw-bg-primary-100 tw-text-primary-800"
                                            >
                                                {{ category.name }}
                                            </span>
                                        </div>
                                        <span v-else class="tw-text-gray-500">No categories assigned</span>
                                    </div>
                                </div>

                                <!-- Company -->
                                <div class="tw-mb-6">
                                    <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Company</label>
                                    <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                                        {{ service.company?.name || 'No company assigned' }}
                                    </div>
                                </div>

                                <!-- Status -->
                                <div class="tw-mb-6">
                                    <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Status</label>
                                    <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                                        <span
                                            :class="[
                                                'tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium',
                                                service.status === 'active' 
                                                    ? 'tw-bg-green-100 tw-text-green-800' 
                                                    : 'tw-bg-red-100 tw-text-red-800'
                                            ]"
                                        >
                                            {{ service.status === 'active' ? 'Active' : 'Inactive' }}
                                        </span>
                                    </div>
                                </div>

                                <!-- Created Date -->
                                <div class="tw-mb-6">
                                    <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Created Date</label>
                                    <div class="tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                                        {{ formatDate(service.created_at) }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Right Column - Service Image -->
                        <div class="tw-space-y-6">
                            <div>
                                <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Service Image</h2>
                                
                                <!-- Image Display -->
                                <div class="tw-mb-6">
                                    <div class="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-bg-gray-50 tw-min-h-[300px] tw-flex tw-items-center tw-justify-center">
                                        <img
                                            v-if="service.image"
                                            :src="service.image"
                                            :alt="service.name"
                                            class="tw-max-w-full tw-max-h-72 tw-object-cover tw-rounded-lg tw-shadow-sm"
                                        />
                                        <div v-else class="tw-text-center tw-text-gray-500">
                                            <icon icon="solar:settings-bold-duotone" class="tw-text-6xl tw-text-gray-400 tw-mb-2" />
                                            <p>No image available</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Error State -->
                <div v-else class="tw-text-center tw-py-12">
                    <div class="tw-text-gray-500">
                        <icon icon="solar:danger-circle-bold-duotone" class="tw-text-6xl tw-text-red-400 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-medium tw-mb-2">Service Not Found</h3>
                        <p class="tw-mb-4">The service you're looking for doesn't exist or has been deleted.</p>
                        <a-button
                            @click="goBack"
                            intent="primary"
                        >
                            Back to Services
                        </a-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import useAlert from '@/composables/useAlert'
import * as serviceApi from '@/api/services'

const router = useRouter()
const route = useRoute()

// State
const loading = ref(true)
const service = ref(null)

// Methods
const goBack = () => {
    router.push({ name: 'pages.admin.services' })
}

const formatDate = (date) => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const loadService = async () => {
    loading.value = true
    
    try {
        const serviceId = route.params.id
        
        // Use admin endpoint if available, otherwise fall back to regular endpoint
        const response = serviceApi.admin && serviceApi.admin.get 
            ? await serviceApi.admin.get(serviceId)
            : await serviceApi.get(serviceId)
        
        if (response.data) {
            service.value = response.data
        } else {
            throw new Error('Service not found')
        }
    } catch (error) {
        console.error('Error loading service:', error)
        
        // Handle different error types
        if (error.response?.status === 404) {
            useAlert('Service not found', 'danger')
        } else if (error.response?.status === 401) {
            useAlert('Authentication required. Please log in again.', 'danger')
        } else if (error.response?.status === 403) {
            useAlert('You do not have permission to view this service.', 'danger')
        } else if (error.response?.status >= 500) {
            useAlert('Server error. Please try again later.', 'danger')
        } else {
            useAlert('Error loading service details. Please try again.', 'danger')
        }
        
        service.value = null
    } finally {
        loading.value = false
    }
}

// Initialize
onMounted(() => {
    loadService()
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style> 